import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, ChevronRight, Sparkles, PartyPopper } from "lucide-react";

// Real configuration checklist: every state comes from /onboarding/setup-status (computed from tenant data).
export default function SetupChecklist({ isAdmin }) {
  const [st, setSt] = useState(null);
  const nav = useNavigate();
  const { t } = useTranslation();
  const load = () => http.get("/onboarding/setup-status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => {
    load();
    window.addEventListener("nm-tour-changed", load);
    return () => window.removeEventListener("nm-tour-changed", load);
  }, []);
  if (!st) return null;

  if (st.all_required_done) {
    return (
      <Card data-testid="setup-complete-card" className="p-4 border-emerald-200 bg-emerald-50 flex items-start gap-3">
        <PartyPopper className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
        <div className="text-sm">
          <div className="font-semibold text-emerald-800">{t("checklist.complete_title")}</div>
          <div className="text-emerald-700">{t("checklist.complete_body")}</div>
        </div>
      </Card>
    );
  }

  return (
    <Card data-testid="setup-checklist" className="p-5 border-slate-200">
      <div className="flex items-center justify-between mb-2 gap-3">
        <div className="font-semibold text-[#0F172A] flex items-center gap-2"><Sparkles className="w-4 h-4 text-sky-500" /> {t("checklist.title")}</div>
        <span className="text-sm font-bold text-sky-600" data-testid="setup-percent">{t("checklist.percent", { p: st.percent })}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-4"><div className="h-full bg-sky-500 transition-all" style={{ width: `${st.percent}%` }} data-testid="setup-progress-bar" /></div>
      <div className="grid sm:grid-cols-2 gap-1.5">
        {st.items.map((it) => (
          <button key={it.key} type="button" onClick={() => isAdmin && nav(it.to)} disabled={!isAdmin}
            className="flex items-center gap-2 text-sm text-left rounded-lg px-2 py-1.5 hover:bg-slate-50 disabled:cursor-default group"
            data-testid={`setup-item-${it.key}`} data-done={it.done ? "1" : "0"}>
            {it.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
            <span className={`flex-1 ${it.done ? "text-slate-400 line-through" : "text-slate-700"}`}>
              {t(`checklist.item_${it.key}`)}{!it.required && <span className="text-slate-400 no-underline"> · {t("checklist.optional")}</span>}
              {it.key === "catalog" && it.needs_pricelist && !it.done && <span className="block text-[11px] text-amber-600">{t("checklist.catalog_pricelist_hint")}</span>}
            </span>
            {isAdmin && !it.done && <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-sky-500" />}
          </button>
        ))}
      </div>
      {isAdmin && st.next && (
        <div className="flex flex-wrap gap-2 mt-4">
          <Button data-testid="setup-continue-btn" onClick={() => nav(st.next.to)} className="bg-sky-600 hover:bg-sky-700 gap-1.5">
            {t("checklist.continue_config")}: {t(`checklist.item_${st.next.key}`)} <ChevronRight className="w-4 h-4" />
          </Button>
          <Button data-testid="setup-resume-tour-btn" variant="outline" onClick={() => window.dispatchEvent(new Event("nm-resume-tour"))} className="gap-1.5">
            <Sparkles className="w-4 h-4" /> {t("checklist.resume_tour")}
          </Button>
        </div>
      )}
    </Card>
  );
}
